import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@xbto/design-system';
import { createColumnHelper } from '@tanstack/react-table';
import { BigOrZero } from 'common';
import { Col, Row, TextBody, TextSup, TextTitle } from '../../atoms';
import { Footnote, LoadingIndicator } from '../../molecules';
import { AmountChangePercent, ErrorCallout, Table } from '../../organisms';
import { FundPerformanceInSelect, PerformanceSettings, } from '../fund-performance-in-select';
const monthToName = {
    1: 'Jan',
    2: 'Feb',
    3: 'Mar',
    4: 'Apr',
    5: 'May',
    6: 'Jun',
    7: 'Jul',
    8: 'Aug',
    9: 'Sep',
    10: 'Oct',
    11: 'Nov',
    12: 'Dec',
};
export default function ComponentWeb({ data, errored, loading, onRowPressed, onPerformanceInPress, trackPerformanceInClicked, hidePerformanceInSelector = false, }) {
    const [performanceIn, setPerformanceIn] = useState(PerformanceSettings.ASSET_ACCUMULATION);
    const isDisabled = loading || !!errored;
    const handlePerformanceInPress = useCallback((val) => {
        setPerformanceIn(val);
        if (onPerformanceInPress) {
            onPerformanceInPress(val);
        }
    }, [onPerformanceInPress]);
    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('year', {
            cell: info => {
                return _jsx(TextBody, { color: "primary", children: info.getValue() });
            },
            size: 84,
            header: () => (_jsx(TextBody, { small: true, em: true, lhFull: true, children: "Year" })),
            meta: {
                paddingHorizontal: theme.spacing['4'],
                justify: 'flex-start',
                cursor: 'initial',
                active: true,
            },
        }),
        ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => columnHelper.accessor(row => { var _a; return (_a = row === null || row === void 0 ? void 0 : row[month]) === null || _a === void 0 ? void 0 : _a.performancePercent; }, {
            id: `${month}.performancePercent`,
            cell: info => {
                return info.getValue() === null ||
                    info.getValue() === undefined ? (
                // Explicitly render empty cell for months with no data to avoid height issues
                _jsx(TextBody, { children: " " })) : (_jsx(AmountChangePercent, { value: BigOrZero(info.getValue()) }));
            },
            size: 84,
            header: () => (_jsx(TextBody, { small: true, em: true, lhFull: true, children: monthToName[month] })),
            meta: {
                paddingHorizontal: theme.spacing['4'],
                justify: 'flex-end',
                cursor: 'initial',
                active: true,
            },
        })),
        columnHelper.accessor('ytd.performancePercent', {
            cell: info => (_jsx(AmountChangePercent, { value: BigOrZero(info.getValue()) })),
            size: 85,
            header: () => (_jsx(TextBody, { small: true, em: true, lhFull: true, children: "YTD" })),
            meta: {
                paddingHorizontal: theme.spacing['4'],
                justify: 'flex-end',
                cursor: 'initial',
                active: true,
                backgroundColor: 'tertiary',
                autoWidth: true,
            },
        }),
    ], [columnHelper]);
    return (_jsxs(Col, { style: $.container, children: [_jsxs(Row.LR, { style: $.header, children: [_jsx(TextTitle, { small: true, children: "Investor Month-on-Month Performance (net of fees)*" }), !hidePerformanceInSelector && (_jsx(FundPerformanceInSelect, { performanceIn: performanceIn, setPerformanceIn: handlePerformanceInPress, trackPerformanceInClicked: trackPerformanceInClicked, disabled: isDisabled }))] }), _jsx(View, { style: $.tableContainer, children: loading ? (_jsx(LoadingIndicator, { style: {
                        height: 262,
                        borderWidth: 1,
                        borderColor: theme.color.border.primary,
                        borderRadius: theme.radius.md,
                    } })) : errored ? (_jsx(ErrorCallout, { title: "Unable to display performance", message: "We are unable to display performance at the moment, please try again later", height: 262 })) : (_jsx(Table
                // @ts-expect-error TS is not able to infer the type correctly
                , { 
                    // @ts-expect-error TS is not able to infer the type correctly
                    data: data || [], columns: columns, onRowPressed: onRowPressed })) }), !isDisabled && (_jsxs(Footnote, { children: [_jsx(TextSup, { children: "*" }), "Investor performance are displayed on this page, which may differ from the fund(s) overall performance due to varying investor subscription and redemption periods."] }))] }));
}
const $ = StyleSheet.create({
    container: {
        rowGap: theme.spacing[6],
    },
    header: {
        flexWrap: 'wrap',
        rowGap: theme.spacing[4],
        columnGap: theme.spacing[4],
    },
    tableContainer: {
        flex: 1,
    },
});
